// One-time PG-only backfill of pre-existing local chats on first login after
// chat persistence ships.
//
// Scope is deliberately narrow: NO conversation-history backfill. This makes
// old local chats READABLE everywhere by minting a server id for each local
// chat that lacks one and marking it dirty, so the normal outbox drain pushes
// the render transcript to the server; an old chat's captured conversation
// reaches the analytics store later, on its first continued exchange, not here.
// Attribution is automatic — the sync API pins the chat to the authenticated
// user.
//
// Runs at most once per (browser, tenant, user): a localStorage sentinel guards it so
// a reload doesn't re-scan. Gated by the persistence flag — with the flag off
// it's inert, which is what makes first-activation the flag's one job.
//
// The IDB database and sentinel share the authenticated tenant-and-user owner;
// unattributed legacy rows are discarded instead of being claimed on login.

import { chatPersistenceEnabled } from "./featureFlags.js";
import { mintServerChatId } from "./chatServerId.js";
import { loadAllChatMeta, loadChat, saveChat, getOutboxEntry, putOutboxEntry } from "./chatStorage.js";
import { visibleChatMetas } from "./chatIds.js";
import { isSupportedSchemaVersion } from "./chatSerializer.js";
import { initialOutbox, markDirty } from "./chatOutbox.js";
import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

const SENTINEL_PREFIX = "origin-chat-backfilled-v2";
const LEGACY_SENTINEL_PREFIX = "origin-chat-backfilled";

function sentinelKey(tenantId) {
    discardLegacyTenantEntry(globalThis.localStorage, LEGACY_SENTINEL_PREFIX, tenantId);
    return browserStorageKey(SENTINEL_PREFIX, tenantId);
}

export function backfillAlreadyRan(tenantId) {
    try {
        const key = sentinelKey(tenantId);
        return key ? !!localStorage.getItem(key) : false;
    } catch {
        return false;
    }
}

function markBackfillRan(tenantId) {
    try { const key = sentinelKey(tenantId); if (key) localStorage.setItem(key, "1"); } catch { /* noop */ }
}

// Mint server ids for local chats that lack them and mark them dirty so the
// next outbox drain pushes them. Returns the count of chats prepared. The
// caller runs the drain afterwards. Idempotent + once-guarded.
export async function runBackfillOnce(tenantId) {
    if (!chatPersistenceEnabled()) return { prepared: 0, skipped: "flag-off" };
    if (backfillAlreadyRan(tenantId)) return { prepared: 0, skipped: "already-ran" };

    const metas = visibleChatMetas(await loadAllChatMeta(tenantId), isSupportedSchemaVersion);
    let prepared = 0;
    for (const meta of metas) {
        // A chat seeded by restore (or already backfilled) already carries a
        // server id — leave it to the normal sync path.
        if (meta.serverChatId) continue;
        const body = await loadChat(tenantId, meta.localId);
        if (!body) continue;
        const serverChatId = body.serverChatId || mintServerChatId();
        if (!body.serverChatId) {
            // Default the title here rather than in buildChatHead: the head is
            // deliberately tri-state (absent leaves the stored value alone), so
            // defaulting there would let a title-less local record overwrite a
            // good server title. A legacy/imported body with no title key is
            // the one writer that pushes a payload it didn't build, and it used
            // to land as an empty-string title on the server row.
            await saveChat(tenantId, meta.localId, { ...body, chatId: String(meta.localId), serverChatId, title: body.title || "New chat" });
        }
        const existing = await getOutboxEntry(tenantId, meta.localId);
        const entry = existing
            ? markDirty({ ...existing, serverChatId }, { now: Date.now() })
            : initialOutbox({ serverChatId, now: Date.now() });
        await putOutboxEntry(tenantId, meta.localId, entry);
        prepared++;
    }
    markBackfillRan(tenantId);
    return { prepared };
}

// Test-only reset of the once-guard.
export function _resetBackfillSentinel(tenantId) {
    try { const key = sentinelKey(tenantId); if (key) localStorage.removeItem(key); } catch { /* noop */ }
}
